/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
/* eslint-disable multiline-ternary */
import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { AddCircleOutline, DeleteOutline, ExpandMore } from "@mui/icons-material";
import { Button } from "components/haven-design/buttons";
import { BuilderQuestion } from "./form-builder-utils";
import {
  applyQuestionDataPresets,
  getFixedSelectOptionPreset,
  isUsingFixedSelectOptionPreset,
  SelectOptionPreset,
  SOCIAL_SECURITY_NUMBER_DATA_PRESET,
} from "./form-builder-question-data-presets";

type Props = {
  question: BuilderQuestion;
  questionType: string;
  onUpdateQuestion: (
    questionId: string,
    update: (question: BuilderQuestion) => BuilderQuestion,
  ) => void;
};

const asObject = (value: unknown): Record<string, unknown> => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  return value as Record<string, unknown>;
};

const parseQuestionData = (dataJson: string): {
  data: Record<string, unknown>;
  error: string;
} => {
  if (!dataJson.trim()) return { data: {}, error: "" };

  try {
    const parsed = JSON.parse(dataJson);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return {
        data: {},
        error: "Question data must be a JSON object.",
      };
    }

    return {
      data: parsed as Record<string, unknown>,
      error: "",
    };
  } catch (error) {
    return {
      data: {},
      error: `Invalid question data JSON: ${String(error)}`,
    };
  }
};

const toOptionObjects = (value: unknown): Record<string, unknown>[] => {
  if (!Array.isArray(value)) return [];
  return value.map((optionValue) => asObject(optionValue));
};

const FormBuilderQuestionDataEditor: React.FC<Props> = ({
  question,
  questionType,
  onUpdateQuestion,
}) => {
  const { data: questionData, error: questionDataError } = React.useMemo(
    () => parseQuestionData(question.dataJson),
    [question.dataJson],
  );
  const questionName = question.name.trim();
  const fixedOptionsPreset = getFixedSelectOptionPreset(questionName);
  const hasFixedOptionsPreset = Boolean(fixedOptionsPreset) &&
    (questionType === "select" || questionType === "multiple");
  const fixedOptionsWereRestored = hasFixedOptionsPreset &&
    !isUsingFixedSelectOptionPreset(questionName, questionData.options);
  const isSocialSecurityNumberQuestion = questionType === "text" && questionName === "social_security_number";
  const currentOptions = toOptionObjects(questionData.options);
  const selectLayout = asObject(questionData.layout);
  const selectLayoutIsRadio = `${selectLayout.type || ""}` === "radio";
  const selectLayoutWrap = Boolean(selectLayout.wrap);

  const updateQuestionData = React.useCallback(
    (update: (data: Record<string, unknown>) => Record<string, unknown>) => {
      onUpdateQuestion(question.draftId, (currentQuestion) => {
        const { data: currentData } = parseQuestionData(currentQuestion.dataJson);
        const nextData = update(currentData);

        return {
          ...currentQuestion,
          dataJson: JSON.stringify(nextData, null, 2),
        };
      });
    },
    [onUpdateQuestion, question.draftId],
  );

  React.useEffect(() => {
    if (questionDataError) return;

    const presetData = applyQuestionDataPresets(questionName, questionType, questionData);
    if (JSON.stringify(presetData) === JSON.stringify(questionData)) return;

    onUpdateQuestion(question.draftId, (currentQuestion) => ({
      ...currentQuestion,
      dataJson: JSON.stringify(presetData, null, 2),
    }));
  }, [
    questionDataError,
    questionName,
    questionType,
    questionData,
    onUpdateQuestion,
    question.draftId,
  ]);

  const renderFixedOptions = (options: SelectOptionPreset[]) => (
    <Stack spacing={0.5}>
      <Typography variant="body2" fontWeight={600}>
        Options
      </Typography>
      {options.map((option) => (
        <Typography key={`${option.value}-${option.label}`} variant="caption" color="text.secondary">
          {option.label} ({option.value || "<empty>"})
        </Typography>
      ))}
      <Typography variant="caption" color="text.secondary">
        This question uses a fixed option set.
      </Typography>
    </Stack>
  );

  const renderOptionEditor = () => (
    <Stack spacing={1}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="body2" fontWeight={600}>
          Options
        </Typography>
        <Button
          size="S"
          type="button"
          color="secondary"
          onClick={() => {
            updateQuestionData((currentData) => ({
              ...currentData,
              options: [...toOptionObjects(currentData.options), { value: "", label: "" }],
            }));
          }}
        >
          <AddCircleOutline fontSize="small" style={{ marginRight: "0.3rem" }} />
          Add Option
        </Button>
      </Stack>
      {currentOptions.length === 0 && (
        <Typography variant="caption" color="text.secondary">
          No options yet.
        </Typography>
      )}
      {currentOptions.map((option, index) => (
        <Grid key={`${question.draftId}-option-${index}`} container spacing={1}>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              size="small"
              fullWidth
              label="Value"
              value={`${option.value || ""}`}
              onChange={(event) => {
                const { value } = event.target;
                updateQuestionData((currentData) => {
                  const options = toOptionObjects(currentData.options);
                  const currentOption = options[index] || {};

                  options[index] = {
                    ...currentOption,
                    value,
                  };

                  return {
                    ...currentData,
                    options,
                  };
                });
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 7 }}>
            <TextField
              size="small"
              fullWidth
              label="Label"
              value={`${option.label || ""}`}
              onChange={(event) => {
                const label = event.target.value;
                updateQuestionData((currentData) => {
                  const options = toOptionObjects(currentData.options);
                  const currentOption = options[index] || {};

                  options[index] = {
                    ...currentOption,
                    label,
                  };

                  return {
                    ...currentData,
                    options,
                  };
                });
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 1 }} sx={{ display: "flex", justifyContent: "flex-end" }}>
            <IconButton
              aria-label="Delete option"
              onClick={() => {
                updateQuestionData((currentData) => {
                  const options = toOptionObjects(currentData.options)
                    .filter((_option, optionIndex) => optionIndex !== index);

                  return {
                    ...currentData,
                    options,
                  };
                });
              }}
            >
              <DeleteOutline />
            </IconButton>
          </Grid>
        </Grid>
      ))}
    </Stack>
  );

  const renderDataByType = () => {
    if (questionDataError) {
      return (
        <Typography variant="caption" color="error.main">
          {questionDataError}
        </Typography>
      );
    }

    if (questionType === "formatted") {
      return (
        <TextField
          fullWidth
          multiline
          minRows={5}
          label="Formatted Content (Markdown)"
          value={`${questionData.content || ""}`}
          onChange={(event) => {
            const content = event.target.value;
            updateQuestionData((currentData) => ({ ...currentData, content }));
          }}
          helperText="Saved to question.data.content"
        />
      );
    }

    if (questionType === "array") {
      return (
        <TextField
          size="small"
          fullWidth
          label="Array Label"
          value={`${questionData.label || ""}`}
          onChange={(event) => {
            const label = event.target.value;
            updateQuestionData((currentData) => ({ ...currentData, label }));
          }}
          helperText="Saved to question.data.label"
        />
      );
    }

    if (questionType === "text") {
      return (
        <Grid container spacing={1}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              size="small"
              fullWidth
              label="Placeholder"
              value={`${questionData.placeholder || ""}`}
              onChange={(event) => {
                const placeholder = event.target.value;
                updateQuestionData((currentData) => ({ ...currentData, placeholder }));
              }}
              disabled={isSocialSecurityNumberQuestion}
              helperText={
                isSocialSecurityNumberQuestion
                  ? "Fixed for social_security_number."
                  : "Saved to question.data.placeholder"
              }
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              size="small"
              fullWidth
              label="Mask"
              value={`${questionData.mask || ""}`}
              onChange={(event) => {
                const mask = event.target.value;
                updateQuestionData((currentData) => ({ ...currentData, mask }));
              }}
              disabled={isSocialSecurityNumberQuestion}
              helperText={
                isSocialSecurityNumberQuestion
                  ? `Fixed mask: ${SOCIAL_SECURITY_NUMBER_DATA_PRESET.mask}.`
                  : "Saved to question.data.mask"
              }
            />
          </Grid>
        </Grid>
      );
    }

    if (questionType === "select" || questionType === "multiple") {
      const optionsEditor = hasFixedOptionsPreset && fixedOptionsPreset
        ? renderFixedOptions(fixedOptionsPreset)
        : renderOptionEditor();

      return (
        <Stack spacing={1}>
          {optionsEditor}
          {questionType === "select" && (
            <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={selectLayoutIsRadio}
                    onChange={(event) => {
                      const { checked } = event.target;
                      updateQuestionData((currentData) => {
                        if (!checked) {
                          const { layout: _layout, ...rest } = currentData;
                          return rest;
                        }

                        const currentLayout = asObject(currentData.layout);
                        return {
                          ...currentData,
                          layout: {
                            ...currentLayout,
                            type: "radio",
                            wrap: Boolean(currentLayout.wrap),
                          },
                        };
                      });
                    }}
                  />
                )}
                label="Radio Layout"
              />
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={selectLayoutWrap}
                    disabled={!selectLayoutIsRadio}
                    onChange={(event) => {
                      const { checked } = event.target;
                      updateQuestionData((currentData) => ({
                        ...currentData,
                        layout: {
                          type: "radio",
                          wrap: checked,
                        },
                      }));
                    }}
                  />
                )}
                label="Inline (wrap)"
              />
            </Stack>
          )}
          {fixedOptionsWereRestored && (
            <Typography variant="caption" color="text.secondary">
              Fixed options were restored automatically for this question.
            </Typography>
          )}
        </Stack>
      );
    }

    return (
      <Typography variant="caption" color="text.secondary">
        No structured editor for this question type.
      </Typography>
    );
  };

  return (
    <Stack spacing={1}>
      {renderDataByType()}
      <Accordion disableGutters>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="body2" fontWeight={600}>
            Advanced Question Data (JSON)
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <TextField
            fullWidth
            multiline
            minRows={4}
            label="Question Data (JSON)"
            value={question.dataJson}
            onChange={(event) => {
              onUpdateQuestion(question.draftId, (current) => ({
                ...current,
                dataJson: event.target.value,
              }));
            }}
            error={Boolean(questionDataError)}
            helperText={questionDataError || "Use only for fields not covered above."}
          />
        </AccordionDetails>
      </Accordion>
    </Stack>
  );
};

export default FormBuilderQuestionDataEditor;
